<template>
  <div class="m-b-lg">
    <!-- With a title: title left, month + Today right. Without one, the month
         is the row's label — anchor it left rather than clumping it against
         the Today button in the right corner. -->
    <div class="flex align-items-center m-b-md gap-sm justify-space-between">
      <h2 v-if="title" class="font-bold m-y-none">{{ title }}</h2>
      <span v-else class="text-gray-600 dark-text-gray-300">{{ formatWithMoment(value, 'MMMM YYYY') }}</span>
      <div class="flex align-items-center gap-sm flex-shrink-0">
        <span v-if="title" class="text-gray-600 dark-text-gray-300">{{ formatWithMoment(value, 'MMMM YYYY') }}</span>
        <button @click="goToToday" class="btn btn-default btn-outline">Today</button>
      </div>
    </div>

    <div class="overflow-x-auto">
      <div class="flex gap-xs" style="padding-bottom: 8px;">
        <div v-if="allowPast" class="flex flex-column align-items-center" style="min-width: 60px;">
          <button
            @click="showEarlierDates"
            :disabled="loadingEarlier"
            aria-label="Show earlier dates"
            class="rounded-full w-12 h-12 flex justify-center align-items-center m-b-xs bg-white border border-solid border-gray-200 hover:bg-gray-100"
          >
            <i v-if="loadingEarlier" class="fa-solid fa-spinner fa-spin"></i>
            <i v-else class="fa-solid fa-chevron-left"></i>
          </button>
          <span class="text-12 text-gray-600">Earlier</span>
        </div>
        <div
          v-for="date in visibleDates"
          :key="date.format('YYYY-MM-DD')"
          class="flex flex-column align-items-center"
          style="min-width: 60px;"
        >
          <button
            @click="selectDate(date)"
            class="rounded-full w-12 h-12 flex justify-center align-items-center m-b-xs"
            :class="[
              isSelectedDate(date) ? 'bg-green-700 text-green-50 border border-solid border-2 border-green-600' : 'bg-gray-100 hover:bg-gray-200 text-gray-800 border border-solid border-gray-200',
              !hasEventsOnDate(date) ? 'opacity-25' : '',
            ]"
          >
            {{ date.format('D') }}
          </button>
          <span class="text-12 text-gray-600">{{ date.format('ddd') }}</span>
        </div>

        <div v-if="showMore" class="flex flex-column align-items-center" style="min-width: 60px;">
          <button
            @click="showMoreDates"
            :disabled="loadingMore"
            class="rounded-full w-12 h-12 flex justify-center align-items-center m-b-xs bg-white border border-solid border-gray-200 hover:bg-gray-100"
          >
            <i v-if="loadingMore" class="fa-solid fa-spinner fa-spin"></i>
            <i v-else class="fa-solid fa-plus"></i>
          </button>
          <span class="text-12 text-gray-600">More</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formattingMixin } from './mixins/formatting_mix';

// Horizontal day-strip date picker: a scrollable row of day circles starting
// from today, with dimmed days that have nothing bookable. Owns only the strip;
// the parent owns the selected date (v-model, a moment) and whatever list
// renders beneath it.
export default {
  name: 'date-strip-picker',
  mixins: [formattingMixin],
  props: {
    // Selected date as a moment (v-model).
    value: { type: Object, required: true },
    // 'YYYY-MM-DD' strings for days that have something bookable — anything
    // else renders dimmed. Days outside this list are still selectable so the
    // user can confirm a day really has nothing.
    activeDates: { type: Array, default: () => [] },
    // Empty string hides the heading and left-anchors the month label — for
    // contexts where the card already has its own heading.
    title: { type: String, default: 'Choose a time' },
    // Hide the "More" button when the parent loads its whole window up front.
    showMore: { type: Boolean, default: true },
    loadingMore: { type: Boolean, default: false },
    // Show the "Earlier" button so the strip can walk backwards in time.
    // Off by default: the parent booking flow must stay today-forward.
    allowPast: { type: Boolean, default: false },
    loadingEarlier: { type: Boolean, default: false },
    daysPerPage: { type: Number, default: 30 },
  },
  data() {
    return {
      visibleDates: this.generateVisibleDatesFrom(moment()),
    };
  },
  computed: {
    activeDateSet() {
      return new Set(this.activeDates);
    },
  },
  watch: {
    // When the parent jumps to a date outside the strip (e.g. "Next available
    // time"), restart the strip from that date so the selection is visible.
    value(newDate) {
      if (newDate && !this.visibleDates.some((d) => d.isSame(newDate, 'day'))) {
        this.visibleDates = this.generateVisibleDatesFrom(newDate);
      }
    },
  },
  created() {
    if (this.value && !this.visibleDates.some((d) => d.isSame(this.value, 'day'))) {
      this.visibleDates = this.generateVisibleDatesFrom(this.value);
    }
  },
  methods: {
    selectDate(date) {
      this.$emit('input', date);
    },
    goToToday() {
      this.visibleDates = this.generateVisibleDatesFrom(moment());
      this.selectDate(moment());
    },
    isSelectedDate(date) {
      return this.value && this.value.isSame(date, 'day');
    },
    hasEventsOnDate(date) {
      return this.activeDateSet.has(date.format('YYYY-MM-DD'));
    },
    generateVisibleDatesFrom(startMoment) {
      const dates = [];
      const currentDate = moment(startMoment);
      for (let i = 0; i < this.daysPerPage; i += 1) {
        dates.push(currentDate.clone());
        currentDate.add(1, 'day');
      }
      return dates;
    },
    showMoreDates() {
      if (this.loadingMore) return;

      const lastDate = this.visibleDates[this.visibleDates.length - 1];
      const newDates = [];
      const currentDate = moment(lastDate).add(1, 'day');
      for (let i = 0; i < this.daysPerPage; i += 1) {
        newDates.push(currentDate.clone());
        currentDate.add(1, 'day');
      }
      this.visibleDates = [...this.visibleDates, ...newDates];

      // Parents that fetch availability in windows listen for this and load
      // the newly-visible range.
      this.$emit('load-more', {
        startAt: newDates[0].clone().startOf('day'),
        endAt: newDates[newDates.length - 1].clone().endOf('day'),
      });
    },
    showEarlierDates() {
      if (this.loadingEarlier) return;

      const firstDate = this.visibleDates[0];
      const newDates = [];
      const currentDate = moment(firstDate).subtract(this.daysPerPage, 'days');
      for (let i = 0; i < this.daysPerPage; i += 1) {
        newDates.push(currentDate.clone());
        currentDate.add(1, 'day');
      }
      this.visibleDates = [...newDates, ...this.visibleDates];

      this.$emit('load-earlier', {
        startAt: newDates[0].clone().startOf('day'),
        endAt: newDates[newDates.length - 1].clone().endOf('day'),
      });
    },
  },
};
</script>
